import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { BrandMark } from "@/components/BrandLogo";
import { RefreshCw, Home, AlertTriangle } from "lucide-react";

/** Friendly branded error screen (function component → can use hooks for i18n). */
export function ErrorFallback({ error, onReset }) {
  let t = (k, f) => f;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    t = useLanguage().t;
  } catch (e) {
    /* rendered outside LanguageProvider — use English fallbacks */
  }
  const isDev = process.env.NODE_ENV === "development";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-6 py-16" role="alert">
      <div className="brand-glow h-80 w-80 bg-primary/25" style={{ top: "10%", left: "15%" }} aria-hidden="true" />
      <div className="brand-glow h-64 w-64 bg-gold/10" style={{ bottom: "5%", right: "10%", animationDelay: "-6s" }} aria-hidden="true" />

      <div className="glass-card relative w-full max-w-lg p-8 text-center sm:p-10">
        <div className="mx-auto mb-6 flex w-fit items-center gap-3">
          <BrandMark size="lg" />
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-destructive/15 text-destructive">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </span>
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {t("found.errorTitle", "Something went wrong")}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
          {t("found.errorDesc", "An unexpected error occurred. Reload the page — if the problem continues, contact our support team.")}
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="focus-ring inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition hover:bg-primary/90"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {t("found.reload", "Reload page")}
          </button>
          <a
            href="/"
            onClick={() => onReset?.()}
            className="focus-ring inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-card px-5 text-sm font-semibold text-foreground transition hover:bg-muted"
          >
            <Home className="h-4 w-4" aria-hidden="true" />
            {t("found.goHome", "Go to home")}
          </a>
        </div>

        {isDev && error && (
          <details className="mt-8 text-left">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
              {t("found.errorDetails", "Technical details")}
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-muted p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
              {String(error?.stack || error?.message || error)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

/**
 * Top-level error boundary. Class component (required for componentDidCatch);
 * i18n is handled by the ErrorFallback function component.
 * Resets automatically when `resetKey` changes (e.g. on route change).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Stale lazy chunk after a deploy → one automatic hard reload
    const msg = String(error?.message || "");
    if (/Loading chunk [\w-]+ failed|ChunkLoadError|Failed to fetch dynamically imported module/i.test(msg)) {
      try {
        if (!sessionStorage.getItem("eam_chunk_reload")) {
          sessionStorage.setItem("eam_chunk_reload", "1");
          window.location.reload();
          return;
        }
      } catch (e) {
        /* ignore */
      }
    }
    if (process.env.NODE_ENV !== "production") {
      console.error("[ErrorBoundary]", error, info?.componentStack);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset() {
    this.setState({ error: null });
  }

  render() {
    if (this.state.error) {
      return this.props.fallback
        ? this.props.fallback({ error: this.state.error, reset: this.reset })
        : <ErrorFallback error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}
